import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import type { Dimensions } from 'ember-container-query';
import { capitalize } from 'frontend-utils/utils/convert-case';
import type HotelParityInsightController from 'lumen/controllers/hotel/parity/insight';
import type { RouterServiceWithLegacyTransitionTo } from 'lumen/initializers/router-legacy-transition-to';
import type { BreakdownItemsPerExtractDate } from 'lumen/models/parity/insight/historical-data';
import type ParityInsightSegmentationItem from 'lumen/models/parity/insight/segmentation-item';
import type HotelParityInsightTrendsRoute from 'lumen/routes/hotel/parity/insight/trends';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import { MEALTYPE_TRANSLATION_KEYS } from 'lumen/utils/constants/general-constants';
import type {
  Breakdown,
  DateAggregation,
  ParityFilter,
  Percentages,
  SortType,
} from 'lumen/utils/constants/pi-constant-values';
import {
  DEFAULT_LOS,
  DEFAULT_POS,
  HISTORICAL_POINTS_AMOUNT,
  PA_SORT_TYPES,
  PARITY_FILTER,
  RATE_TYPE_TRANSLATIONS,
} from 'lumen/utils/constants/pi-constant-values';
import getTodayUtc from 'lumen/utils/dates/get-today-utc';
import type { ActiveFilter } from 'lumen/utils/filter-bar/filter-functions';
import type { ModelFrom } from 'lumen/utils/routes';

type ViolatorSeries = {
  dataPoints: {
    extractDate: string;
    violator: ParityInsightSegmentationItem;
  }[];
  item: ParityInsightSegmentationItem;
};

type InvertedTrendsData = ViolatorSeries[];

export default class HotelParityInsightTrendsController extends Controller {
  declare model: ModelFrom<HotelParityInsightTrendsRoute>;

  queryParams = ['segmentation', 'selection', 'trendsExtractDate'];

  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: RouterServiceWithLegacyTransitionTo;
  @service declare simpleExport: Services['simple-export'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @controller('hotel.parity.insight')
  declare parentController: HotelParityInsightController;

  @tracked filterBarHeight = 0;
  @tracked modalContentHeight = 600;
  @tracked segmentation: Breakdown | null = null;
  @tracked selection: ParityFilter =
    DEFAULT_QP_VALUES.market.parity.insight.trends.selection;
  @tracked tabsContainerHeaderHeight = 0;
  @tracked trendsExtractDate: string | null = null;

  get activeFilters(): ActiveFilter[] {
    return this.parentController.activeFilters;
  }

  get beginReached(): boolean {
    const nextTrendsExtractDate = dayjs(this.trendsExtractDate).subtract(
      HISTORICAL_POINTS_AMOUNT,
      this.dateAggregation,
    );

    return nextTrendsExtractDate.isBefore(
      this.hotelAndMarketService.hotelOrMarket.parityFirstExtractDate,
    );
  }

  get chartHeight(): number {
    // container-query can't be attached to the Modal+TabsContainer content
    // TabsContainer header and filter bar height are saved (once on render) and subtracted from the modal content height
    return (
      this.modalContentHeight -
      this.tabsContainerHeaderHeight -
      this.filterBarHeight
    );
  }

  get dateAggregation(): DateAggregation {
    return this.parentController.dateAggregation;
  }

  get endReached(): boolean {
    const nextTrendsExtractDate = dayjs(this.trendsExtractDate).add(
      1,
      this.dateAggregation,
    );

    return nextTrendsExtractDate.isAfter(getTodayUtc(), this.dateAggregation);
  }

  /**
   * All filters grouped by their segmentation blocks as a array containing comma-separated strings:
   * ['Market: EMEA, America', 'Channel: Expedia']
   * @return {Array} An array containing all filters per segmentation
   */
  get filtersPerSegmentation(): string[] {
    return this.activeFilters.map((activeFilter) => {
      const translatedSegmentation = this.intl.t(activeFilter.translationKey, {
        count: 1,
      });

      // Using capitalize here to ensure capitalisation in Excel sheets
      const translatedFilters = activeFilter.values.map((value) =>
        capitalize(value.displayValue.toString()),
      );

      return `${translatedSegmentation}: ${translatedFilters.join(', ')}`;
    });
  }

  get filterString(): string {
    const filters: string[] = [];

    if (this.parentController.showMemberRate) {
      filters.push(this.intl.t('parity_strategy.membership.member'));
    }

    filters.push(
      this.intl.t(RATE_TYPE_TRANSLATIONS[this.parentController.rateType]),
    );

    if (this.parentController.los !== DEFAULT_LOS) {
      filters.push(
        this.intl.t('general.los', { los: this.parentController.los }),
      );
    }

    filters.push(
      this.intl.t(MEALTYPE_TRANSLATION_KEYS[this.parentController.mealPlan]),
    );

    if (this.parentController.pos !== DEFAULT_POS) {
      filters.push(
        `${this.intl.t('general.pos_short')}: ${this.parentController.pos}`,
      );
    }

    return [...filters, ...this.filtersPerSegmentation].join(' - ');
  }

  get formattedPeriod(): string {
    if (this.dateAggregation === 'month') {
      return `${dayjs(this.trendsExtractDate)
        .subtract(HISTORICAL_POINTS_AMOUNT - 1, 'month')
        .format(this.dates.partialDateFormat.YEAR_AND_MONTH_LONG)} - ${dayjs(
        this.trendsExtractDate,
      ).format(this.dates.partialDateFormat.YEAR_AND_MONTH_LONG)}`;
    }

    return `${dayjs(this.trendsExtractDate)
      .subtract(HISTORICAL_POINTS_AMOUNT - 1, 'week')
      .format(
        'll',
      )} - ${dayjs(this.trendsExtractDate).add(6, 'day').format('ll')}`;
  }

  get isExportButtonDisabled(): boolean {
    if (this.isLoading || !this.model.trendsData.value) {
      return true;
    }

    const breakdownItemsPerExtractData = this.model.trendsData.value;
    // Checks extract dates and entries to see if there is at least some data for export
    const hasSomeData = Object.values(breakdownItemsPerExtractData).some(
      (data) => data.length,
    );

    return !hasSomeData || this.isLoading;
  }

  get isLoading(): boolean {
    return this.model.trendsData.isRunning;
  }

  get sortType(): SortType {
    return this.parentController.sortType;
  }

  get trendsData(): InvertedTrendsData {
    if (this.isLoading || !this.model.trendsData.value) {
      return [];
    }

    if (Object.keys(this.model.trendsData.value).length) {
      const topTenViolators = this.getTopTenViolators(
        this.model.trendsData.value,
        this.selection,
      );
      return this.invertViolatorsAndDates(topTenViolators);
    }

    return [];
  }

  compareBy(a: Percentages, b: Percentages, key: ParityFilter): number {
    const baseOrder = [
      PARITY_FILTER.LOSS,
      PARITY_FILTER.MEET,
      PARITY_FILTER.WIN,
    ] as const;

    const sortingOrder = [key, ...baseOrder.filter((item) => item !== key)] as [
      ParityFilter,
      ParityFilter,
      ParityFilter,
    ];

    if (a[sortingOrder[0]] !== b[sortingOrder[0]]) {
      return b[sortingOrder[0]] - a[sortingOrder[0]];
    }

    if (a[sortingOrder[1]] !== b[sortingOrder[1]]) {
      return b[sortingOrder[1]] - a[sortingOrder[1]];
    }

    return a[sortingOrder[2]] - b[sortingOrder[2]];
  }

  /**
   * Gets the last data object from a segmentation response expected in the following format
   * Expects data like this:
   * data = {
   *   "2018-03-01": [...segmentationValues],
   *   "2018-03-02": [...segmentationValues],
   *   ...
   * }
   * As of 06/06/2018 the API responses are declared to be unsorted even though
   * they might appear as sorted. This is why we add an extra sorting step into this function
   * Instead of doing reverse, we sort ASC
   *
   * @param  {Object} data Data for one single segmentation
   * @return {Array}       Array containing all the segmentation values for the most recent extract date found
   */
  getMostRecentExtractDateData(
    data: BreakdownItemsPerExtractDate,
  ): ParityInsightSegmentationItem[] {
    let result: ParityInsightSegmentationItem[] = [];

    Object.keys(data)
      .sort((a, b) => dayjs.utc(b).unix() - dayjs.utc(a).unix())
      .some((extractDate) => {
        if (data[extractDate]?.length) {
          result = data[extractDate];
          return true;
        }

        return false;
      });

    return result;
  }

  /**
   * For a specific channel and timeline, filter all but the top ten violators
   * Takes the top 10 violators of the most recent extract date as a guide for the others
   * Expects data like this:
   * data = {
   *   date1 = [], <- keeps only the top 10 within each of these arrays
   *   date2 = [],
   *   ...
   * }
   * @param  {Object} data Data for one single segmentation
   * @return {Object}      The same data structure as the input, but with only top ten violators
   */
  getTopTenViolators(
    data: BreakdownItemsPerExtractDate,
    selection: ParityFilter,
  ): BreakdownItemsPerExtractDate {
    const mostRecentExtractDateData = this.getMostRecentExtractDateData(data);
    const sortType = PA_SORT_TYPES[this.sortType];

    // Strip most recent date down to the top ten violators (this will form a guide for the other extract dates)
    // Sorting on loss/meet/win filtered/unfiltered in that order. This will be a complete set!
    const sortedData = mostRecentExtractDateData.sort((a, b) => {
      // We sort according to the loss, meet, win of the values
      const compareValue = this.compareBy(a[sortType], b[sortType], selection);
      if (compareValue !== 0) {
        return compareValue;
      }

      if (
        a.unfilteredData &&
        b.unfilteredData &&
        this.compareBy(
          a.unfilteredData[sortType] as Percentages,
          b.unfilteredData[sortType] as Percentages,
          selection,
        ) !== 0
      ) {
        // If comparing normal filtered values results equal, compare win/meet/loss of the unfiltered data
        return this.compareBy(
          a.unfilteredData[sortType] as Percentages,
          b.unfilteredData[sortType] as Percentages,
          selection,
        );
      }

      return a.segmentationValueId.localeCompare(b.segmentationValueId);
    });

    // From that complete set, take only the top 10. This is now the guide!
    const guide = sortedData.slice(0, 10);
    const topTenViolators: BreakdownItemsPerExtractDate = {};

    // For each extract date on the data, use guide to generate list of top ten violators
    Object.keys(data)
      .sort((a, b) => dayjs.utc(a).unix() - dayjs.utc(b).unix())
      .forEach((extractDate) => {
        topTenViolators[extractDate] = [];

        // For every violator in our guide of top ten violators...
        guide.forEach((violator) => {
          const foundViolator = data[extractDate]?.some((segmentationValue) => {
            const totalNbOfShops =
              segmentationValue.calculatedValues?.totalNbOfShops;
            // Search this extract date until you find the violator
            if (
              segmentationValue.segmentationValue ===
                violator.segmentationValue &&
              totalNbOfShops
            ) {
              // Store the violator into the new object
              topTenViolators[extractDate]?.push(segmentationValue);
              return true;
            }

            return false;
          });

          // It is possible that the value searched for is not found for a different date
          // Or that the violator is found but has no shops
          // In those cases we push the violator from the guide flagged as "no-data"
          if (!foundViolator) {
            const noDataViolator = {
              id: violator.id,
              segmentationValue: violator.segmentationValue,
              hasNoData: true,
              // All the values of the dummy violator object have to be reset!
              value: { win: 0, meet: 0, loss: 0 },
              benchmark: { win: 0, meet: 0, loss: 0 },
              calculatedValues: { win: 0, meet: 0, loss: 0, totalNbOfShops: 0 },
            } as ParityInsightSegmentationItem;

            topTenViolators[extractDate]?.push(noDataViolator);
          }
        });
      });

    return topTenViolators;
  }

  /**
   * For a dataset containing dates and values, invert them so that they are ready for display on a chart
   * Expects data sorted like this:
   * data = {
   *   "2018-03-01": [...violators](10),
   *   "2018-03-02": [...violators](10),
   * }
   *
   * Becomes:
   * data = {
   *   violator1: [{extractDate: "2018-03-01", violator: segmentationValue}, ...](n=weeks),
   *   ...
   * }
   * @param  {[type]} data [description]
   * @return {[type]}      [description]
   */
  invertViolatorsAndDates(
    data: BreakdownItemsPerExtractDate,
  ): InvertedTrendsData {
    const seriesMap = new Map<string, ViolatorSeries>();

    Object.keys(data).forEach((extractDate) => {
      data[extractDate]?.forEach((violator) => {
        const key = violator.segmentationValue;

        if (!key) {
          return;
        }

        if (!seriesMap.has(key)) {
          seriesMap.set(key, {
            item: violator,
            dataPoints: [],
          });
        } else if (!violator.hasNoData && seriesMap.get(key)!.item.hasNoData) {
          seriesMap.get(key)!.item = violator;
        }

        seriesMap.get(key)!.dataPoints.push({
          extractDate,
          violator: violator,
        });
      });
    });

    return [...seriesMap.values()];
  }

  /**
   * Action which changes the history only within the trends modal
   * @param  {Boolean} isForwardClicked True when the user clicked forward
   */
  @action changeExtractDate(isForwardClicked: boolean): void {
    if (isForwardClicked) {
      this.trendsExtractDate = dayjs(this.trendsExtractDate)
        .add(HISTORICAL_POINTS_AMOUNT, this.dateAggregation)
        .format('YYYY-MM-DD');
    } else {
      this.trendsExtractDate = dayjs(this.trendsExtractDate)
        .subtract(HISTORICAL_POINTS_AMOUNT, this.dateAggregation)
        .format('YYYY-MM-DD');
    }
  }

  @action changeSelection(tabName: string): void {
    this.selection = tabName as ParityFilter;
  }

  /**
   * Custom Excel export for trends
   * We compose a table and send it to an endpoint that makes it into an Excel Sheet
   */

  @action
  async exportTrends(): Promise<void> {
    this.usageAnalytics.track('Data Exported', {
      eventTrigger: 'breakdown-detailed-view',
    });

    const sheetName = this.formattedPeriod;
    const headers = [
      this.intl
        .t(`parity_analytics.shop_date.${this.dateAggregation}`)
        .toString(),
      this.intl
        .t(`parity_analytics.${this.segmentation}`, { count: 10 })
        .toString(), // 10 is an arbitraty number to enforce pluralisation
      this.intl
        .t(`parity_analytics.report_${this.selection}_arrival_dates`)
        .toString(),
      `${capitalize(this.selection)} %`,
      this.intl.t('general.total_shops').toString(),
    ];
    const content: (number | string | undefined)[][] = [];

    // Compose filters string with "|" and append the sort type to it
    const title = `${sheetName} [${this.filterString}]`;

    this.trendsData.forEach((series) => {
      const violatorName = series.item.formattedSegmentationValue;
      series.dataPoints.forEach((violatorData) => {
        const value = violatorData.violator.value[this.selection];
        const percentage = this.intl.formatNumber(
          (violatorData.violator.calculatedValues?.[this.selection] ?? 0) * 100,
          { maximumFractionDigits: 2 },
        );
        const total = violatorData.violator.calculatedValues?.totalNbOfShops;
        content.push([
          violatorData.extractDate,
          violatorName,
          value,
          percentage,
          total,
        ]);
      });
    });

    content.sort((a, b) => dayjs.utc(a[0]).unix() - dayjs.utc(b[0]).unix());

    const postData = JSON.stringify({
      headers,
      content,
      title,
      sheetName,
    });

    await this.simpleExport.execute(
      postData,
      this.hotelAndMarketService.hotelOrMarket.id!,
    );
  }

  @action onResizeModal({ dimensions }: { dimensions: Dimensions }): void {
    this.modalContentHeight = dimensions.height;
  }

  @action redirectOnClose(): void {
    this.router.legacyTransitionTo('hotel.parity.insight');
  }

  @action saveFilterBarHeight(element: HTMLElement): void {
    this.filterBarHeight = element.offsetHeight;
  }

  @action saveTabsContainerHeaderHeight(element: HTMLElement): void {
    this.tabsContainerHeaderHeight = element.offsetHeight;
  }
}
